import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import Button from '../../common/Button';
import useDynamicForumFilters from '../../../hooks/useDynamicForumFilters';

const ForumSearch = ({ 
  searchTerm = '', 
  onSearchChange, 
  sortBy = 'recent', 
  onSortChange,
  tags = [],
  onTagsChange,
  questions = [], // Array of questions for dynamic tag filtering
  resultCount = 0,
  className = '',
  currentUserId = null, // Current user ID for "My Posts" filter
  showMyPostsOnly = false,
  onMyPostsToggle
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTags, setSelectedTags] = useState(tags);

  // Get dynamic filter options from questions data
  const { 
    availableTags,
    loading: filtersLoading,
    error: filtersError
  } = useDynamicForumFilters(questions, currentUserId);

  // Handle filter errors gracefully
  const safeAvailableTags = filtersError ? [] : availableTags;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(localSearchTerm);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchTerm, onSearchChange]);

  // Sync external search term changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Sync external tags changes
  useEffect(() => {
    setSelectedTags(tags);
  }, [tags]);

  // Sort options
  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'mostReplies', label: 'Most Replies' }
  ];

  const handleSearchInputChange = (e) => {
    setLocalSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    if (onSearchChange) {
      onSearchChange('');
    }
  };

  const handleSortChange = (newSortBy) => {
    if (onSortChange) {
      onSortChange(newSortBy);
    }
  };

  const handleFilterToggle = (filterValue) => {
    if (filterValue === 'my-posts') {
      // Handle My Posts filter separately
      if (onMyPostsToggle) {
        onMyPostsToggle(!showMyPostsOnly);
      }
    } else {
      // Handle tag filters - we need to convert kebab-case back to Title Case for database queries
      // since existing tags in database are in Title Case format
      const displayFormat = safeAvailableTags.find(f => f.value === filterValue)?.label || filterValue;
      
      const newTags = selectedTags.includes(displayFormat)
        ? selectedTags.filter(t => t !== displayFormat)
        : [...selectedTags, displayFormat];
      
      setSelectedTags(newTags);
      if (onTagsChange) {
        onTagsChange(newTags);
      }
    }
  };

  const handleClearAllFilters = () => {
    setLocalSearchTerm('');
    setSelectedTags([]);
    if (onSearchChange) onSearchChange('');
    if (onTagsChange) onTagsChange([]);
    if (onSortChange) onSortChange('recent');
    if (onMyPostsToggle) onMyPostsToggle(false);
  };

  const hasActiveFilters = localSearchTerm || selectedTags.length > 0 || sortBy !== 'recent' || showMyPostsOnly;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        </div>
        <input
          type="text"
          value={localSearchTerm}
          onChange={handleSearchInputChange}
          placeholder="Search posts..."
          className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 
                     rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white 
                     placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 
                     focus:ring-purple-500 focus:border-transparent transition-all duration-200"
        />
        {localSearchTerm && (
          <button
            onClick={handleClearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 
                       hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
            aria-label="Clear search"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Filter Controls */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* Sort and Filter Toggle */}
        <div className="flex items-center space-x-4">
          {/* Sort Dropdown */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 
                         dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-sm 
                         text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 
                         focus:border-transparent transition-all duration-200"
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Filter Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            disabled={filtersLoading}
            className={`flex items-center space-x-2 ${showFilters ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700' : ''}`}
          >
            <FunnelIcon className="w-4 h-4" />
            <span>
              {filtersLoading ? 'Loading...' : (selectedTags.length > 0 ? `Filters (${selectedTags.length})` : 'Filter')}
            </span>
          </Button>
        </div>

        {/* Results Count and Clear Filters */}
        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
          <span>{resultCount} post{resultCount !== 1 ? 's' : ''}</span>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAllFilters}
              className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
            >
              Clear all
            </Button>
          )}
        </div>
      </div>

      {/* Expanded Filters */}
      {showFilters && safeAvailableTags.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="space-y-4">
            {/* Tags Filter */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Filter by Tags
              </h4>
              <div className="flex flex-wrap gap-2">
                {safeAvailableTags.map(filter => (
                  <button
                    key={filter.value}
                    onClick={() => handleFilterToggle(filter.value)}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium 
                               transition-all duration-200 ${
                      (filter.value === 'my-posts' && showMyPostsOnly) || selectedTags.includes(filter.label)
                        ? 'bg-purple-500 text-white shadow-sm'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-200 dark:hover:border-purple-700'
                    }`}
                  >
                    {filter.label}
                    {filter.count && (
                      <span className="ml-1 text-xs opacity-75">({filter.count})</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show message when no tags are available */}
      {showFilters && safeAvailableTags.length === 0 && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-center text-sm text-gray-500 dark:text-gray-400">
            No tags available for filtering
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {(localSearchTerm || selectedTags.length > 0 || showMyPostsOnly) && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Active filters:</span>
          
          {localSearchTerm && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium 
                           bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              Search: "{localSearchTerm}"
              <button
                onClick={handleClearSearch}
                className="ml-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </span>
          )}

          {showMyPostsOnly && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium 
                           bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              My Posts
              <button
                onClick={() => onMyPostsToggle && onMyPostsToggle(false)}
                className="ml-1.5 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </span>
          )}
          
          {selectedTags.map(tag => {
            // Find the corresponding filter option to get the kebab-case value for removal
            const tagOption = safeAvailableTags.find(t => t.label === tag);
            return (
              <span
                key={tag}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium 
                           bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
              >
                {tag}
                <button
                  onClick={() => handleFilterToggle(tagOption ? tagOption.value : tag)}
                  className="ml-1.5 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ForumSearch;